import React from 'react';
import Head from 'next/head';
import Error from 'next/error';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import type { GetStaticPaths, GetStaticProps } from 'next';

import Layout from '../../../components/layout';
import ArticleResultsList from '../../../components/article/results-list';
import { ListingHeader } from '../../../components/article/ListingHeader';
import {
  client,
  getAllCategoriesWithURI,
  getCategoryBySlugQuery,
  getCategorySEOByURI,
  getPostsByCategoryId
} from '../../../apollo-client';
import { convertSlugToURI, getYoastHeadSEO } from '../../../lib/utils';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { normaliseSanityListingItem } from '@lib/listings/normalise';
import { toPageProps } from '@lib/listings/page-props';
import type { ListingItem } from '@lib/listings/types';
import { getArticleCategory, getListingCardsByCategory } from '@lib/sanity';
import type { SanityArticleCategoryPage } from '@lib/sanity';

export const REVALIDATE_SECONDS = 600;
// Both CMSs confirmed the category does not exist, so the 404 can live as
// long as a hit; transport failures rethrow instead of caching a 404.
export const NOT_FOUND_REVALIDATE_SECONDS = 600;
const CATEGORY_PATH_PREFIX = '/category';

type WordPressCategory = { categoryId: number; name: string; slug?: string };

export type CategoryPageProps = {
  category: WordPressCategory | null;
  categoryId: number | null;
  seo: { fullHead?: string } | null;
  initialArticles: Record<string, unknown> | null;
  sanityCategory: SanityArticleCategoryPage | null;
  sanityItems: ListingItem[];
  slug: string;
  /** Every segment of the category URL, so nested categories keep their canonical. */
  path: string;
};

const Category = ({
  category,
  categoryId,
  seo,
  initialArticles,
  sanityCategory,
  sanityItems,
  slug,
  path
}: CategoryPageProps) => {
  const router = useRouter();

  if (!category && !sanityCategory) {
    return <Error statusCode={404} />;
  }

  const pathname = router.asPath;
  const yoastHead = seo?.fullHead
    ? getYoastHeadSEO(seo.fullHead, pathname)
    : null;
  const title = sanityCategory?.title ?? category?.name ?? slug;
  const canonicalUrl = `${FFH_HOSTNAME}${CATEGORY_PATH_PREFIX}/${path}`;

  return (
    <div>
      <Head>
        {yoastHead ?? (
          <>
            <title>{`${title} | ${SITE_NAME}`}</title>
            {sanityCategory?.description && (
              <meta name="description" content={sanityCategory.description} />
            )}
            <link rel="canonical" href={canonicalUrl} key="canonical" />
          </>
        )}
      </Head>

      <Layout>
        <div className="relative bg-gray-50 pt-4 pb-8 px-4 sm:pt-8 sm:px-6 lg:pt-8 lg:pb-8 lg:px-8">
          <div className="absolute">
            <div className="bg-white h-1/3 sm:h-2/3" />
          </div>
          <ListingHeader
            title={title}
            description={sanityCategory?.description}
          />
          <ArticleResultsList
            category={category}
            type="categories"
            query={categoryId ? getPostsByCategoryId(categoryId) : undefined}
            initialData={initialArticles ?? undefined}
            sanityItems={sanityItems}
          />
        </div>
      </Layout>
    </div>
  );
};

const toSegments = (slug: string | string[] | undefined): string[] =>
  Array.isArray(slug) ? slug : slug ? [slug] : [];

const lastSegment = (slug: string | string[] | undefined): string =>
  Array.isArray(slug) ? slug[slug.length - 1] : slug ?? '';

export const getStaticProps: GetStaticProps<CategoryPageProps> = async (
  context
) => {
  const slugParam = context.params?.slug;
  const slug = lastSegment(slugParam);
  const path = toSegments(slugParam).join('/');
  const uri = `${CATEGORY_PATH_PREFIX}/${convertSlugToURI(slugParam)}`;
  console.info(`Building category: ${slugParam}`);

  try {
    // network-only: the module-level Apollo client persists across ISR
    // revalidations in a warm lambda, so cache-first would pin these
    // queries to whatever the instance fetched first.
    const [categoryQuery, sanityCategory, sanityCards] = await Promise.all([
      client.query({
        query: gql`
          ${getCategoryBySlugQuery(slug)}
        `,
        fetchPolicy: 'network-only'
      }),
      getArticleCategory(slug),
      getListingCardsByCategory(slug)
    ]);

    const category: WordPressCategory | null =
      categoryQuery.data.categories.edges[0]?.node ?? null;
    const sanityItems = toPageProps(
      sanityCards.map(normaliseSanityListingItem)
    );

    if (!category && !sanityCategory) {
      return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
    }

    if (!category) {
      return {
        props: {
          category: null,
          categoryId: null,
          seo: null,
          initialArticles: null,
          sanityCategory,
          sanityItems,
          slug,
          path
        },
        revalidate: REVALIDATE_SECONDS
      };
    }

    // The SEO and posts queries only depend on the category lookup above,
    // not on each other, so run them concurrently.
    const [pageQuery, postsQuery] = await Promise.all([
      client.query({
        query: gql`
          ${getCategorySEOByURI(uri)}
        `,
        fetchPolicy: 'network-only'
      }),
      client.query({
        query: gql`
          ${getPostsByCategoryId(category.categoryId)}
        `,
        fetchPolicy: 'network-only'
      })
    ]);

    return {
      props: {
        seo: pageQuery?.data?.nodeByUri?.seo ?? null,
        category,
        categoryId: category.categoryId,
        initialArticles: postsQuery.data,
        sanityCategory,
        sanityItems,
        slug,
        path
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    console.error(`Failed to fetch category for slug "${slugParam}":`, error);
    // Rethrow so Next.js keeps serving the last good page during
    // background revalidation instead of caching a transient 404.
    throw error;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { data } = await client.query({
    query: gql`
      ${getAllCategoriesWithURI()}
    `
  });

  return {
    paths:
      data.categories.edges.map((category) => `${category.node.uri}`) || [],
    fallback: 'blocking'
  };
};

export default Category;
